
import React from 'react';
import { Send, Instagram, Twitter, Linkedin, Mail } from 'lucide-react';
import type { PageType } from '../types';

interface FooterProps {
  onNavigate: (page: PageType) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  return (
    <footer className="bg-white dark:bg-slate-900 pt-16 pb-8 border-t border-slate-200 dark:border-slate-800 transition-colors duration-300 relative overflow-hidden">
       {/* Background Pattern - Adaptive */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none">
        <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0 0 L100 100 M100 0 L0 100" stroke="currentColor" strokeWidth="0.5" className="text-slate-900 dark:text-white" />
        </svg>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Grid System: 1 col mobile, 2 col tablet, 4 col desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-12">
          
          {/* Brand Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-3xl font-black tracking-tighter leading-none text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-blue-600 dark:from-violet-400 dark:to-blue-400 font-sans">
                F4ST
              </span>
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm leading-7 text-justify">
              سریع‌ترین سرویس کوتاه کننده لینک با امکانات کامل و پیشرفته. ما به شما کمک می‌کنیم تا لینک‌های خود را مدیریت، بررسی و بهینه‌سازی کنید.
            </p>
          </div>

          {/* Links Column */}
          <div>
            <h3 className="font-bold text-slate-800 dark:text-white text-lg mb-5 relative inline-block">
              دسترسی سریع
              <span className="absolute -bottom-2 right-0 w-8 h-1 bg-violet-500 rounded-full"></span>
            </h3>
            <ul className="space-y-3">
              {[
                { title: 'صفحه اصلی', action: () => onNavigate('home') },
                { title: 'تعرفه ها', action: () => onNavigate('monetization') },
                { title: 'امکانات', action: () => onNavigate('about') },
                { title: 'سوالات متداول', action: () => onNavigate('faq') },
              ].map((item, idx) => (
                <li key={idx}>
                  <button onClick={item.action} className="text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors text-sm flex items-center gap-2 group w-full text-right">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700 group-hover:bg-violet-500 transition-colors"></span>
                    {item.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="font-bold text-slate-800 dark:text-white text-lg mb-5 relative inline-block">
              خدمات ما
              <span className="absolute -bottom-2 right-0 w-8 h-1 bg-blue-500 rounded-full"></span>
            </h3>
            <ul className="space-y-3">
              {[
                { title: 'کوتاه کننده لینک', action: () => onNavigate('home') },
                { title: 'ساخت UTM', action: () => onNavigate('utm') },
                { title: 'ساخت QR Code', action: () => onNavigate('qr') },
                { title: 'بررسی لینک مقصد', action: () => onNavigate('checker') },
                { title: 'گزارش تخلف', action: () => onNavigate('report') },
              ].map((item, idx) => (
                <li key={idx}>
                  <button onClick={item.action} className="text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm flex items-center gap-2 group w-full text-right">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700 group-hover:bg-blue-500 transition-colors"></span>
                    {item.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="font-bold text-slate-800 dark:text-white text-lg mb-5 relative inline-block">
              ارتباط با ما
              <span className="absolute -bottom-2 right-0 w-8 h-1 bg-rose-500 rounded-full"></span>
            </h3>
            <ul className="space-y-4 mb-6">
              <li className="flex items-center gap-3 text-slate-500 dark:text-slate-400 text-sm">
                <Mail size={18} className="text-rose-500 shrink-0" />
                <a href="mailto:info@f4st.ir" className="font-sans hover:text-rose-600 dark:hover:text-rose-400 transition-colors dir-ltr">info@f4st.ir</a>
              </li>
            </ul>
            
            <div className="flex gap-3">
              {[
                { icon: Instagram, href: '#', label: 'اینستاگرام' },
                { icon: Twitter, href: '#', label: 'توییتر' },
                { icon: Linkedin, href: '#', label: 'لینکدین' },
                { icon: Send, href: '#', label: 'تلگرام' }
              ].map((Item, idx) => (
                <a 
                  key={idx} 
                  href={Item.href}
                  aria-label={Item.label}
                  className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center justify-center transition-all hover:bg-violet-600 hover:text-white hover:-translate-y-1"
                >
                  <Item.icon size={18} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-slate-200 dark:border-slate-800 pt-8 mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 dark:text-slate-400 text-xs font-medium">
             تمامی حقوق برای <span className="font-bold text-slate-700 dark:text-slate-200">فست</span> محفوظ است © ۱۴۰۳
          </p>
          <div className="flex items-center gap-6">
             <button onClick={() => onNavigate('terms')} className="text-xs text-slate-500 hover:text-violet-600 dark:text-slate-400 dark:hover:text-violet-400 transition-colors">قوانین و مقررات</button>
             <button onClick={() => onNavigate('report')} className="text-xs text-slate-500 hover:text-violet-600 dark:text-slate-400 dark:hover:text-violet-400 transition-colors">گزارش تخلف</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
